import React, { useContext } from "react";

/**
 * The column a panel was opened *at* — set when a jump came from a column
 * reference in a rule (⌘-clicking `from` in `l.from`), so the table can say
 * which of its columns that was.
 *
 * A context rather than a prop threaded through every table: the tables that
 * can be a relation's panel are half a dozen hand-built components, and the
 * highlight is a passing annotation on one of their header cells, not part of
 * what any of them is about.
 */
export const HighlightedColumn = React.createContext<string | undefined>(undefined);

/** Amber rather than the app's blue, which already means "selected". */
const highlightStyle: React.CSSProperties = {
  background: "#fff6dd",
  color: "#7a5c00",
  boxShadow: "inset 0 -3px 0 #e8a33d",
};

interface Props {
  /**
   * The name this column has *in the relation* — `max_charge`, not "Max Charge
   * (MW)". That's the name a rule uses, and so the name a jump arrives with.
   * A header with no column behind it (a solver output, an action) has none.
   */
  column: string;
  style: React.CSSProperties;
  title?: string;
  children: React.ReactNode;
}

/**
 * A table header for one column of a relation, which lights up when the panel
 * was opened at that column — and scrolls itself into view, since a table wide
 * enough to need this is a table wide enough to have hidden it.
 */
const ColumnHeader: React.FC<Props> = ({ column, style, title, children }) => {
  const highlighted = useContext(HighlightedColumn) === column;
  return (
    <th
      style={highlighted ? { ...style, ...highlightStyle } : style}
      title={title}
      ref={(node) => {
        if (highlighted) node?.scrollIntoView({ block: "nearest", inline: "nearest" });
      }}
    >
      {children}
    </th>
  );
};

export default ColumnHeader;
